<script lang="ts">
  import type { ManageAPI } from "~/manage-api/types";
  import { getStoresContext } from "~/stores";
  import type { StoresKey } from "~/stores";
  import { todaysOpeningHoursItem } from "~/util/date-n-time";
  import OpeningHoursRange from "@components/opening-hours/OpeningHoursRange.svelte";
  import CardTitle from '../CardTitle.svelte';
  import { lunchModal } from '../../../stores/lunch';
  
  export let store: ManageAPI.Store;
  export let request_store_key: StoresKey;
  export let _class = "";
  export let imageStyle: string = ""; // For custom image styling
  export let borderRadius: string = "";
  export let borderThickness: string = "";
  export let borderColor: string = "";
  export let borderStyle: string = "";
  export let hoverEffect: string = ""; // translate, scale, or none
    
  const { T, reverse_router, scdata, locale } = getStoresContext(request_store_key);
  $: t = $T;
  
  $: href = $reverse_router.routeStore(store, $scdata);
  $: floor = store.floor?.name || '';
  $: hours_today = todaysOpeningHoursItem(store);
  $: hasOpeningHours = store.unformattedOpeningHours?.length > 0;
  $: hasLunchMenu = store.hasLunchMenu === true;
  
  // Use custom style props if provided, otherwise use site-wide CSS variables
  $: actualBorderRadius = borderRadius || "var(--store-card-border-radius, var(--card-border-radius, 8px))";
  $: actualBorderThickness = borderThickness || "var(--store-card-border-thickness, var(--card-border-thickness, 0))";
  $: actualBorderColor = borderColor || "var(--store-card-border-color, var(--card-border-color, transparent))";
  $: actualBorderStyle = borderStyle || "var(--store-card-border-style, var(--card-border-style, solid))";
  
  // Calculate hover transforms based on hover effect
  // When hoverEffect is empty, we'll use CSS variables from site-wide configurator
  $: hoverCardTransform = "";
  $: hoverImageTransform = "";
  $: {
    if (hoverEffect) {
      if (hoverEffect === "translate") {
        hoverCardTransform = "translateY(-5px)";
        hoverImageTransform = "none";
      } else if (hoverEffect === "scale") {
        hoverCardTransform = "none";
        hoverImageTransform = "scale(1.1)";
      } else if (hoverEffect === "none") {
        hoverCardTransform = "none";
        hoverImageTransform = "none";
      }
    }
  }
  
  // Build style string from computed values if imageStyle is not provided
  $: {
    if (!imageStyle) {
      const styleProps = [];
      if (actualBorderRadius) styleProps.push(`border-radius: ${actualBorderRadius}`);
      if (actualBorderThickness && actualBorderStyle && actualBorderColor) 
        styleProps.push(`border: ${actualBorderThickness} ${actualBorderStyle} ${actualBorderColor}`);
      else {
        if (actualBorderThickness) styleProps.push(`border-width: ${actualBorderThickness}`);
        if (actualBorderStyle) styleProps.push(`border-style: ${actualBorderStyle}`);
        if (actualBorderColor) styleProps.push(`border-color: ${actualBorderColor}`);
      }
      
      if (styleProps.length > 0) {
        imageStyle = styleProps.join('; ');
      }
    }
  }
  
  function openLunchModal(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    lunchModal.open(request_store_key, store.id);
  }
</script>

<a href={href} class="store-card-link" style={hoverEffect ? `--custom-card-hover-transform: ${hoverCardTransform}; --custom-image-hover-transform: ${hoverImageTransform};` : ''}>
  <div class="store-card {_class}">
    <div class="store-header">
      <div class="store-name"><CardTitle title={store.name} {_class}/></div>
    </div>
    
    <div class="store-image" style={imageStyle}>
      {#if store.logoURL}
        <img src={store.logoURL} alt={store.name} class="store-logo" />
      {:else}
        <div class="store-placeholder">
          <span>{store.name[0]}</span>
        </div>
      {/if}
      
      {#if hasLunchMenu}
        <button 
          type="button"
          class="lunch-badge"
          on:click={openLunchModal}
          aria-label={t('common.lunch-list')}
        >
          {t('common.lunch-list')}
        </button>
      {/if}
    </div>
    
    <div class="store-content">
      <div class="store-meta">
        <span class="store-hours">
          {#if hasOpeningHours && hours_today}
            <span class="today-text">{t('common.today')}: </span>
            <span class="hours-text">
              <OpeningHoursRange 
                locale={$locale} 
                item={hours_today} 
                opening_hours={store.unformattedOpeningHours}
                t={t}
              />
            </span>
          {/if}
        </span>
        
        {#if floor}
          <span class="store-floor">{t('common.floor')} {floor}</span>
        {/if}
      </div>
    </div>
  </div>
</a>

<style lang="scss">
  .store-card-link {
    display: block;
    text-decoration: none !important;
    color: inherit;
    transition: transform 0.2s ease-in-out;
    width: 100%;
    
    &:hover {
      @media (min-width: 768px) {
        transform: var(--custom-card-hover-transform, var(--card-hover-transform, none));
        
        img, .store-placeholder {
          transform: var(--custom-image-hover-transform, var(--image-hover-transform, none));
        }
      }
      cursor: pointer;
      text-decoration: none;
      color: inherit;
    }
  }
  
  .store-card {
    height: auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    background-color: transparent;
    width: 100%;
  }
  
  .store-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    .store-name {
      margin: 0;
      padding: 0;
      font-weight: 600;
      line-height: 30px;
      letter-spacing: 1px;
      text-align: left;
      background-color: transparent;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
      width: 100%;
      color: inherit;

      &:hover {
        color: inherit;
        text-decoration: none;
      }
    }
  }
  
  .store-image {
    aspect-ratio: 1;
    width: 100%;
    height: auto;
    overflow: hidden;
    position: relative;
    background-color: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    
    @media (max-width: 1200px) {
      height: auto;
    }
    
    @media (max-width: 992px) {
      height: auto;
    }
    
    @media (max-width: 768px) {
      height: auto;
    }
    
    @media (max-width: 375px) {
      height: auto;
    }
    
    img, .store-placeholder {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      transition: transform 0.3s ease;
    }
    
    .store-placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 48px;
      background-color: #f5f5f5;
      color: #000;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
    
    .lunch-badge {
      position: absolute;
      top: 12px;
      right: 12px;
      border: none;
      padding: 4px 8px;
      font-size: var(--lunch-badge-font-size, calc(var(--font-size-sm) * 0.8));
      font-family: var(--liferay-font-family-base);
      font-weight: 700;
      letter-spacing: 1px;
      text-transform: uppercase;
      cursor: pointer;
      white-space: nowrap;
      z-index: 5;
      transition: all 0.2s ease-in-out;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      background-color: var(--lunch-badge-bg-color);
      color: var(--lunch-badge-text-color);
      border-radius: var(--lunch-badge-border-radius, 4px);
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.15);
      }
    }
  }

  .store-content {
    padding: 0;
    flex: 1;
  }

  .store-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: var(--liferay-font-family-base);
    font-size: 12px;
    color: #000;

    .store-hours {
      color: inherit;
      flex: 1;
    }

    .store-floor {
      font-weight: 700;
      line-height: 18px;
      letter-spacing: 1px;
      padding: 4px 16px;
      text-transform: uppercase;
      margin-left: auto;
      font-size: var(--floor-badge-font-size, 12px);
      background-color: var(--floor-badge-bg-color);
      color: var(--floor-badge-text-color);
      border-radius: var(--floor-badge-border-radius, 4px);
    }
    
    .today-text,
    .hours-text {
      font-family: var(--liferay-font-family-base);
      color: inherit;
    }

    .hours-text {
      font-weight: 700;
    }
  }
</style> 